import { useState } from "react";
import { Infinity as InfinityIcon } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Switch } from "@/components/ui/switch";

const Stat = ({ label, value, testid }) => (
  <div className="rounded-xl bg-[#F7F4EE] p-2.5 text-center">
    <p className="text-lg font-semibold text-[#0D2517]" data-testid={testid}>{value}</p>
    <p className="text-[10px] uppercase tracking-wider text-[#5b6b60]">{label}</p>
  </div>
);

export const UserCard = ({ u, onChange }) => {
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const isPro = u.role === "professional";
  const run = async (path, body, ok) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${u.user_id}/${path}`, body);
      onChange(data);
      toast.success(ok);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const give = (n) => run("credits", { amount: n }, `Accreditate ${n} analisi a ${u.email}`);
  const giveCustom = () => {
    const n = parseInt(custom, 10);
    if (!n || n < 1 || n > 1000) return toast.error("Inserisci un numero tra 1 e 1000");
    give(n);
    setCustom("");
  };
  const t = `user-${u.user_id}`;
  const fullName = [u.first_name, u.last_name].filter(Boolean).join(" ");

  return (
    <div className="space-y-3 rounded-2xl border border-[#E2DCD0] bg-white p-4" data-testid={t}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold" data-testid={`${t}-name`}>{fullName || <span className="italic text-[#8a958d]">{u.name || "Nome non indicato"}</span>}</p>
          <p className="truncate text-xs text-[#5b6b60]" data-testid={`${t}-email`}>{u.email}</p>
          <p className="text-[11px] text-[#8a958d]">Registrato il {fmtDate(u.created_at)}</p>
        </div>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${u.unlimited ? "bg-[#0D2517] text-[#C9A96E]" : "bg-[#C9A96E]/20 text-[#7a5d2a]"}`} data-testid={`${t}-status`}>
          {u.unlimited ? "Illimitato" : "Standard"}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Stat label="Completate" value={u.analyses_completed} testid={`${t}-completed`} />
        <Stat label="Disponibili" value={u.unlimited ? "∞" : u.analysis_credits} testid={`${t}-credits`} />
      </div>
      <div className="grid grid-cols-3 gap-2">
        {[1, 5, 10].map((n) => (
          <button key={n} disabled={busy} onClick={() => give(n)} className="chip" data-testid={`${t}-give-${n}`}>+{n}</button>
        ))}
      </div>
      <div className="flex gap-2">
        <input className="field !py-2" type="number" min="1" max="1000" inputMode="numeric" placeholder="Altro numero" value={custom}
          onChange={(e) => setCustom(e.target.value)} data-testid={`${t}-custom-input`} />
        <button disabled={busy} onClick={giveCustom} className="btn-primary shrink-0 !px-4 !py-2 text-xs" data-testid={`${t}-custom-submit`}>Accredita</button>
      </div>
      <label className="flex items-center justify-between rounded-xl bg-[#F7F4EE] px-3 py-2.5 text-sm">
        <span className="flex items-center gap-2"><InfinityIcon size={15} className="text-[#B39257]" /> Account Illimitato</span>
        <Switch checked={u.unlimited} disabled={busy || isPro} data-testid={`${t}-unlimited-switch`}
          onCheckedChange={(v) => run("unlimited", { unlimited: v }, v ? "Account impostato come Illimitato" : "Account riportato a Standard")} />
      </label>
      {isPro && <p className="text-[11px] text-[#8a958d]">L'account amministratore resta sempre Illimitato.</p>}
    </div>
  );
};
